import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { graphql } from 'react-apollo';
import gql from 'graphql-tag';
import InputSlider from '../InputSlider/InputSlider';
import { useOnboardingState } from '../../contexts/OnboardingContext';

// GraphQL mutation for updating user data
const UPDATE_CURRENT_USER = gql`
  mutation updateCurrentUser($product_mix: ProductMixInput) {
    updateCurrentUser(product_mix: $product_mix)
  }
`;

// Create a higher-order component to connect the component to context
const withOnboardingContext = WrappedComponent => {
  return props => {
    const context = useOnboardingState() || {};
    return <WrappedComponent {...props} onboardingContext={context} />;
  };
};

// P&C Lines array to display and track
const P_AND_C_LINES = [
  { id: 'home_ifp', label: 'Home Insurance' },
  { id: 'commercial_ifp', label: 'Commercial Insurance' },
  { id: 'private_products_ifp', label: 'Private Products' },
  { id: 'crop_hail_with_wind_ifp', label: 'Crop Hail with Wind' },
  { id: 'life_ifp', label: 'Life Insurance' },
  { id: 'auto_ifp', label: 'Auto Insurance' },
  { id: 'mcpi_ifp', label: 'MCPI' },
  { id: 'crop_hail_ifp', label: 'Crop Hail' },
  { id: 'farm_insurance_ifp', label: 'Farm Insurance' },
  { id: 'replant_supplement_ifp', label: 'Replant Supplement' },
  { id: 'health_ifp', label: 'Health Insurance' }
];

// Default areas of focus
const AREAS_OF_FOCUS = [
  { id: 'personal_insurance', label: 'Personal Insurance' },
  { id: 'commercial_insurance', label: 'Commercial Insurance' },
  { id: 'farm_coverage', label: 'Farm Coverage' },
  { id: 'life_insurance', label: 'Life Insurance' },
  { id: 'group_benefits', label: 'Group Benefits' },
  { id: 'risk_management', label: 'Risk Management' },
  { id: 'specialized_coverage', label: 'Specialized Coverage' }
];

class PandCLinesOfBusinessStep extends React.Component {
  constructor(props) {
    super(props);
    
    // Initialize the state with values from props or with defaults
    const productMix = props.user?.product_mix || {};
    
    // Initialize state with existing values or 0
    this.state = {
      // For each P&C line, initialize state with current value or 0
      ...P_AND_C_LINES.reduce((acc, line) => {
        acc[line.id] = productMix[line.id] || 0;
        acc[`${line.id}_max`] = productMix[line.id] || 100; // Max values for validation
        return acc;
      }, {}),
      
      // Initialize collections needed for rendering
      pAndCLinesOfBusiness: {},
      areasOfFocus: {},
      availablePAndCLinesOfBusiness: P_AND_C_LINES.map(line => ({ value: line.id, label: line.label })),
      availableAreasOfFocus: AREAS_OF_FOCUS.map(area => ({ value: area.id, label: area.label })),
      
      // Track form validity and loading state
      formValid: true,
      loading: false,
      mutateLoading: false,
      error: null,
      errors: {},
      
      // Total percentage (should sum to 100)
      total: this.calculateTotal(productMix)
    };
    
    // Bind methods
    this.handleSliderChange = this.handleSliderChange.bind(this);
    this.handleContinue = this.handleContinue.bind(this);
    this.handleBack = this.handleBack.bind(this);
    this.saveProductMix = this.saveProductMix.bind(this);
    this.validateForm = this.validateForm.bind(this);
    this.isLineOfBusinessSelected = this.isLineOfBusinessSelected.bind(this);
    this.toggleLineOfBusiness = this.toggleLineOfBusiness.bind(this);
    this.handleLOBPercentageChange = this.handleLOBPercentageChange.bind(this);
    this.calculateTotalPercentage = this.calculateTotalPercentage.bind(this);
    this.isAreaOfFocusSelected = this.isAreaOfFocusSelected.bind(this);
    this.toggleAreaOfFocus = this.toggleAreaOfFocus.bind(this);
    this.handleAreaPercentageChange = this.handleAreaPercentageChange.bind(this);
    this.calculateTotalAreaPercentage = this.calculateTotalAreaPercentage.bind(this);
  }
  
  componentDidMount() {
    // Initialize from user data if available
    const { user } = this.props;
    
    // Initialize product mix from user data
    if (user && user.product_mix) {
      const pAndCLinesOfBusiness = { ...this.state.pAndCLinesOfBusiness };
      
      // Process each line of business from user data
      Object.entries(user.product_mix).forEach(([key, value]) => {
        // Only process product mix fields (not areas of focus)
        if (value > 0 && key.includes('_ifp')) {
          pAndCLinesOfBusiness[key] = value;
        }
      });
      
      this.setState({ pAndCLinesOfBusiness });
    }
    
    // Initialize areas of focus from user data
    if (user && user.areas_of_focus) {
      const areasOfFocus = { ...this.state.areasOfFocus };
      
      // Process user's areas of focus data
      Object.entries(user.areas_of_focus).forEach(([key, value]) => {
        if (value > 0) {
          areasOfFocus[key] = value;
        }
      });
      
      this.setState({ areasOfFocus });
    } else if (user && user.product_mix) {
      // If no explicit areas_of_focus but we have product_mix, try to extract focus areas
      // Map product_mix fields that might be areas of focus to our UI keys
      const areasOfFocus = { ...this.state.areasOfFocus };
      const possibleFocusAreas = {
        'personal_insurance_ifp': 'personal_insurance',
        'commercial_ifp': 'commercial_insurance',
        'farm_insurance_ifp': 'farm_coverage',
        'life_ifp': 'life_insurance',
        'group_ifp': 'group_benefits',
        'risk_management_ifp': 'risk_management',
        'specialized_coverage_ifp': 'specialized_coverage'
      };
      
      // Check for each possible focus area
      Object.entries(possibleFocusAreas).forEach(([productKey, focusKey]) => {
        if (user.product_mix[productKey] && user.product_mix[productKey] > 0) {
          areasOfFocus[focusKey] = user.product_mix[productKey];
        }
      });
      
      // Also check for any keys that might start with 'focus_'
      Object.entries(user.product_mix).forEach(([key, value]) => {
        if (key.startsWith('focus_') && value > 0) {
          const focusKey = key.replace('focus_', '');
          areasOfFocus[focusKey] = value;
        }
      });
      
      this.setState({ areasOfFocus });
    }
    
    // Validate form initially
    this.validateForm();
    
    // Update context form validity
    const { onboardingContext } = this.props;
    if (onboardingContext && typeof onboardingContext.setStepValid === 'function') {
      onboardingContext.setStepValid(this.state.formValid);
    }
  }
  
  calculateTotal(productMix) {
    return P_AND_C_LINES.reduce((sum, line) => sum + (productMix[line.id] || 0), 0);
  }
  
  handleSliderChange(field, value) {
    this.setState({ [field]: value }, () => {
      this.validateForm();
    });
  }
  
  validateForm() {
    // Calculate totals from the P&C lines and areas of focus
    const totalPandC = this.calculateTotalPercentage();
    const totalAreas = this.calculateTotalAreaPercentage();
    
    // Check if any areas are selected (don't require perfect 100% for validation)
    const hasSelectedPandC = Object.values(this.state.pAndCLinesOfBusiness).some(val => val > 0);
    const isValid = hasSelectedPandC && totalPandC <= 100;
    
    // Update errors object if needed
    const errors = {};
    if (totalPandC > 100) {
      errors.pAndCLinesOfBusiness = this.getTranslatedText('error.exceed');
    }
    
    if (totalAreas > 100) {
      errors.areasOfFocus = this.getTranslatedText('error.exceed');
    }
    
    this.setState({ 
      formValid: isValid,
      total: totalPandC,
      errors
    });
    
    // Update context step validity
    const { onboardingContext } = this.props;
    if (onboardingContext && typeof onboardingContext.setStepValid === 'function') {
      onboardingContext.setStepValid(isValid);
    }
    
    return isValid;
  }
  
  async saveProductMix() {
    if (!this.validateForm()) {
      this.setState({ error: this.getTranslatedText('error.exceed') });
      return false;
    }
    
    try {
      this.setState({ loading: true, error: null });
      
      // Use the pAndCLinesOfBusiness object directly
      const productMix = { ...this.state.pAndCLinesOfBusiness };
      
      // Keep areas of focus separate for local state
      const areasOfFocus = { ...this.state.areasOfFocus };
      
      // Only include known valid fields in ProductMixInput
      // Based on ProductMix.jsx and the GraphQL schema
      const validProductMixFields = [
        'home_ifp',
        'commercial_ifp',
        'private_products_ifp',
        'crop_hail_with_wind_ifp',
        'life_ifp',
        'auto_ifp',
        'mcpi_ifp',
        'crop_hail_ifp',
        'farm_insurance_ifp',
        'replant_supplement_ifp',
        'health_ifp',
        'group_ifp',
        'mutual_funds_aum',
        'securities_aum',
        'annuities_aua',
        'segregated_funds_aua',
        'gic_fixed_income',
        'insurance',
        'equity'
      ];
      
      // Only send fields that are valid in the schema
      const sanitizedProductMix = {};
      Object.keys(productMix).forEach(key => {
        if (validProductMixFields.includes(key)) {
          sanitizedProductMix[key] = productMix[key];
        }
      });
      
      // Execute GraphQL mutation with only valid fields
      const response = await this.props.mutate({
        variables: {
          product_mix: sanitizedProductMix
        }
      });
      
      const data = response.data;
      
      if (data && data.updateCurrentUser) {
        // Update user data in context
        if (this.props.onUserUpdate) {
          this.props.onUserUpdate({
            product_mix: productMix,
            areas_of_focus: areasOfFocus
          });
        }
        
        // Update window.__WIZARD_DATA__ if it exists
        if (window.__WIZARD_DATA__ && window.__WIZARD_DATA__.resumeData) {
          window.__WIZARD_DATA__.resumeData = {
            ...window.__WIZARD_DATA__.resumeData,
            product_mix: productMix,
            areas_of_focus: areasOfFocus
          };
        }
        
        // Update context user
        const { onboardingContext } = this.props;
        if (onboardingContext && typeof onboardingContext.updateUser === 'function') {
          onboardingContext.updateUser({
            product_mix: productMix,
            areas_of_focus: areasOfFocus
          });
        }
        
        this.setState({ loading: false });
        return true;
      } else {
        throw new Error('Failed to update product mix');
      }
    } catch (err) {
      console.error('Error saving product mix:', err);
      this.setState({ 
        error: this.getTranslatedText('error.save'),
        loading: false
      });
      return false;
    }
  }
  
  async handleContinue() {
    try {
      const saveSuccessful = await this.saveProductMix();
      
      if (saveSuccessful) {
        // Prioritize props.onNext (Redux/OnboardingWizard) which saves to server
        if (typeof this.props.onNext === 'function') {
          this.props.onNext();
          return true;
        }
        
        // Fallback to context navigation if props.onNext not available
        const { onboardingContext } = this.props;
        if (onboardingContext && typeof onboardingContext.goToNextStep === 'function') {
          onboardingContext.goToNextStep();
          return true;
        }
        
        // Last resort: direct URL navigation
        window.location.href = '/onboarding/headline_and_about_me';
        return true;
      }
      
      return false;
    } catch (err) {
      console.error("Error in continue button click handler:", err);
      this.setState({ error: this.getTranslatedText('error.navigate') });
      return false;
    }
  }
  
  handleBack() {
    // Prefer props navigation (from OnboardingWizard) over context navigation
    // This ensures proper synchronization when used inside OnboardingWizard
    if (typeof this.props.onBack === 'function') {
      this.props.onBack();
      return;
    }
    
    // Try using context navigation as fallback
    const { onboardingContext } = this.props;
    if (onboardingContext && typeof onboardingContext.goToPreviousStep === 'function') {
      onboardingContext.goToPreviousStep();
      return;
    }
    
    // Final fallback to direct URL navigation
    window.location.href = '/onboarding/practice_information';
  }
  
  // Add missing methods for handling lines of business
  isLineOfBusinessSelected(lobId) {
    return this.state.pAndCLinesOfBusiness[lobId] > 0;
  }
  
  toggleLineOfBusiness(lobId) {
    const isSelected = this.isLineOfBusinessSelected(lobId);
    const newState = { ...this.state.pAndCLinesOfBusiness };
    
    if (isSelected) {
      newState[lobId] = 0;
    } else {
      newState[lobId] = 10; // Default value when adding
    }
    
    this.setState({ pAndCLinesOfBusiness: newState }, this.validateForm);
  }
  
  handleLOBPercentageChange(lobId, value) {
    const percentValue = parseInt(value, 10);
    const newState = { ...this.state.pAndCLinesOfBusiness };
    
    // Calculate what the current total would be without this field
    const otherFieldsTotal = Object.keys(newState).reduce((total, key) => {
      if (key === lobId) {
        return total; // Don't include the current item we're changing
      }
      return total + (parseInt(newState[key]) || 0);
    }, 0);
    
    // Calculate the maximum allowed value for this field
    const maxAllowed = 100 - otherFieldsTotal;
    
    // Ensure the new value doesn't cause total to exceed 100%
    const finalValue = Math.min(percentValue, maxAllowed);
    
    // If value is 0, remove from selection, otherwise set the value
    if (finalValue === 0) {
      newState[lobId] = 0;
    } else {
      newState[lobId] = finalValue;
    }
    
    this.setState({ pAndCLinesOfBusiness: newState }, this.validateForm);
  }
  
  calculateTotalPercentage() {
    const { pAndCLinesOfBusiness } = this.state;
    return Object.values(pAndCLinesOfBusiness).reduce((total, val) => total + (parseInt(val) || 0), 0);
  }
  
  // Add missing methods for handling areas of focus
  isAreaOfFocusSelected(areaId) {
    return this.state.areasOfFocus[areaId] > 0;
  }
  
  toggleAreaOfFocus(areaId) {
    const isSelected = this.isAreaOfFocusSelected(areaId);
    const newState = { ...this.state.areasOfFocus };
    
    if (isSelected) {
      newState[areaId] = 0;
    } else {
      newState[areaId] = 10; // Default value when adding
    }
    
    this.setState({ areasOfFocus: newState }, this.validateForm);
  }
  
  handleAreaPercentageChange(areaId, value) {
    const percentValue = parseInt(value, 10);
    const newState = { ...this.state.areasOfFocus };
    
    // Calculate what the current total would be without this field
    const otherFieldsTotal = Object.keys(newState).reduce((total, key) => {
      if (key === areaId) {
        return total; // Don't include the current item we're changing
      }
      return total + (parseInt(newState[key]) || 0);
    }, 0);
    
    // Calculate the maximum allowed value for this field
    const maxAllowed = 100 - otherFieldsTotal;
    
    // Ensure the new value doesn't cause total to exceed 100%
    const finalValue = Math.min(percentValue, maxAllowed);
    
    // If value is 0, remove from selection, otherwise set the value
    if (finalValue === 0) {
      newState[areaId] = 0;
    } else {
      newState[areaId] = finalValue;
    }
    
    this.setState({ areasOfFocus: newState }, this.validateForm);
  }
  
  calculateTotalAreaPercentage() {
    const { areasOfFocus } = this.state;
    return Object.values(areasOfFocus).reduce((total, val) => total + (parseInt(val) || 0), 0);
  }
  
  // Get translated text based on current language
  getTranslatedText(key) {
    const { currentLanguage } = this.props;
    
    // Define translations
    const translations = {
      en: {
        'title': 'Property & Casualty Lines of Business',
        'subtitle': 'Tell us about the lines of business and areas you focus on.',
        'lines.of.business': 'Lines of Business',
        'lines.of.business.desc': 'Select the lines of business you focus on in your practice.',
        'areas.of.focus': 'Areas of Focus',
        'areas.of.focus.desc': 'Select the specific areas you specialize in.',
        'loading.lines': 'Loading lines of business...',
        'loading.areas': 'Loading areas of focus...',
        'total': 'Total:',
        'total.warning': 'Total percentages must equal 100%',
        'percent.min': '0%',
        'percent.max': '100%',
        'back': 'Back',
        'continue': 'Continue',
        'saving': 'Saving...',
        'error.save': 'There was a problem saving your information. Please try again.',
        'error.navigate': 'There was a problem navigating to the next step. Please try again.',
        'error.exceed': 'Total percentage cannot exceed 100%. Please adjust your selections.'
      },
      fr: {
        'title': 'Branches d\'assurance IARD',
        'subtitle': 'Informez-nous des branches d\'assurance et des domaines sur lesquels vous vous concentrez.',
        'lines.of.business': 'Branches d\'assurance',
        'lines.of.business.desc': 'Sélectionnez les branches d\'assurance sur lesquelles vous vous concentrez dans votre pratique.',
        'areas.of.focus': 'Domaines de spécialisation',
        'areas.of.focus.desc': 'Sélectionnez les domaines spécifiques dans lesquels vous vous spécialisez.',
        'loading.lines': 'Chargement des branches d\'assurance...',
        'loading.areas': 'Chargement des domaines de spécialisation...',
        'total': 'Total:',
        'total.warning': 'Le pourcentage total doit être égal à 100%',
        'percent.min': '0%',
        'percent.max': '100%',
        'back': 'Retour',
        'continue': 'Continuer',
        'saving': 'Enregistrement...',
        'error.save': 'Un problème est survenu lors de l\'enregistrement de vos informations. Veuillez réessayer.',
        'error.navigate': 'Un problème est survenu lors de la navigation vers l\'étape suivante. Veuillez réessayer.',
        'error.exceed': 'Le pourcentage total ne peut pas dépasser 100%. Veuillez ajuster vos sélections.'
      }
    };
    
    // Return translated text or default to English
    return translations[currentLanguage]?.[key] || translations['en'][key] || key;
  }
  
  render() {
    const { 
      pAndCLinesOfBusiness, 
      areasOfFocus,
      availablePAndCLinesOfBusiness, 
      availableAreasOfFocus,
      loading, 
      mutateLoading,
      formValid,
      errors = {} 
    } = this.state;
    
    // Get current language from props or default to 'en'
    const currentLanguage = this.props.currentLanguage || 'en';

    return (
      <div className="max-w-4xl mx-auto">
        <div className="space-y-8">
          {/* Lines of Business Section */}
          <div className="bg-succession-bg-primary shadow-succession-sm rounded-lg overflow-hidden hover:shadow-succession-md transition-all duration-300" style={{ backgroundColor: 'var(--succession-bg-primary, #ffffff)' }}>
            <div className="p-6">
              <h3 className="text-lg font-medium text-succession-text-primary border-b border-succession-border pb-3 mb-4">{this.getTranslatedText('lines.of.business')}</h3>
              <p className="text-sm text-succession-text-secondary mb-4">
                {this.getTranslatedText('lines.of.business.desc')}
              </p>
              
              {errors.pAndCLinesOfBusiness && (
                <p className="mb-4 text-sm text-succession-error bg-succession-error-bg rounded-md p-2.5 flex items-center">
                  <svg className="h-4 w-4 mr-2 text-succession-error" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                  {errors.pAndCLinesOfBusiness}
                </p>
              )}
              
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
                {availablePAndCLinesOfBusiness.length === 0 ? (
                  <div className="col-span-full flex justify-center items-center p-8">
                    <svg className="animate-spin h-6 w-6 text-succession-primary mr-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    <span className="text-succession-text-muted">{this.getTranslatedText('loading.lines')}</span>
                  </div>
                ) : (
                  availablePAndCLinesOfBusiness.map(lob => {
                    const isSelected = this.isLineOfBusinessSelected(lob.value);
                    const checkboxColor = isSelected ? 'text-succession-primary' : 'text-succession-text-muted';
                    
                    return (
                      <div 
                        key={lob.value}
                        className={`relative p-4 rounded-lg border ${isSelected ? 'border-succession-primary bg-succession-primary-10' : 'border-succession-border bg-succession-bg-primary'} hover:border-succession-primary transition-all duration-200 group`}
                      >
                        <div 
                          className="flex items-start justify-between cursor-pointer"
                          onClick={() => this.toggleLineOfBusiness(lob.value)}
                        >
                          <div className="flex-1">
                            <div className="flex items-center">
                              <div className={`flex-shrink-0 h-5 w-5 ${checkboxColor} transition-colors duration-200`}>
                                <svg className={`h-5 w-5 ${isSelected ? 'opacity-100' : 'opacity-0'} group-hover:opacity-50 transition-opacity duration-200`} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                </svg>
                              </div>
                              <div className="ml-3">
                                <h4 className={`text-sm font-medium ${isSelected ? 'text-succession-primary' : 'text-succession-text-primary'} transition-colors duration-200`}>
                                  {lob.label}
                                </h4>
                              </div>
                            </div>
                          </div>
                        </div>
                        
                        {isSelected && pAndCLinesOfBusiness[lob.value] && (
                          <div className="mt-3 pl-8 pr-3">
                            <div className="flex items-center justify-between">
                                                          <span className="text-xs text-succession-text-muted">{this.getTranslatedText('percent.min')}</span>
                            <span className="text-xs font-medium text-succession-primary">
                              {pAndCLinesOfBusiness[lob.value]}%
                            </span>
                            <span className="text-xs text-succession-text-muted">{this.getTranslatedText('percent.max')}</span>
                            </div>
                            <input
                              type="range"
                              min="0"
                              max="100"
                              step="5"
                              value={pAndCLinesOfBusiness[lob.value]}
                              onClick={(e) => e.stopPropagation()}
                              onMouseDown={(e) => e.stopPropagation()}
                              onChange={(e) => {
                                e.stopPropagation();
                                this.handleLOBPercentageChange(lob.value, e.target.value);
                              }}
                              className="w-full h-2 bg-gray-200 border border-succession-border rounded-lg appearance-none cursor-pointer accent-succession-primary mt-1"
                              style={{ accentColor: 'var(--succession-primary, #5489B6)' }}
                            />
                          </div>
                        )}
                      </div>
                    );
                  })
                )}
              </div>
              
              {/* Percentage Total Warning */}
              <div className="mt-4 flex items-center justify-between">
                <div className="flex items-center">
                  <span className="text-sm font-medium text-succession-text-primary mr-2">{this.getTranslatedText('total')}</span>
                  <span className={`text-sm font-bold ${this.calculateTotalPercentage() === 100 ? 'text-green-600' : 'text-amber-600'}`}>
                    {this.calculateTotalPercentage()}%
                  </span>
                </div>
                
                {this.calculateTotalPercentage() !== 100 && (
                  <div className="text-sm text-amber-600 flex items-center">
                    <svg className="h-4 w-4 mr-1" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                    </svg>
                    {this.getTranslatedText('total.warning')}
                  </div>
                )}
              </div>
            </div>
          </div>
          
          {/* Areas of Focus Section */}
          <div className="bg-succession-bg-primary shadow-succession-sm rounded-lg overflow-hidden hover:shadow-succession-md transition-all duration-300" style={{ backgroundColor: 'var(--succession-bg-primary, #ffffff)' }}>
            <div className="p-6">
              <h3 className="text-lg font-medium text-succession-text-primary border-b border-succession-border pb-3 mb-4">{this.getTranslatedText('areas.of.focus')}</h3>
              <p className="text-sm text-succession-text-secondary mb-4">
                {this.getTranslatedText('areas.of.focus.desc')}
              </p>
              
              {errors.areasOfFocus && (
                <p className="mb-4 text-sm text-succession-error bg-succession-error-bg rounded-md p-2.5 flex items-center">
                  <svg className="h-4 w-4 mr-2 text-succession-error" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                  {errors.areasOfFocus}
                </p>
              )}
              
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
                {availableAreasOfFocus.length === 0 ? (
                  <div className="col-span-full flex justify-center items-center p-8">
                    <svg className="animate-spin h-6 w-6 text-succession-primary mr-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    <span className="text-succession-text-muted">{this.getTranslatedText('loading.areas')}</span>
                  </div>
                ) : (
                  availableAreasOfFocus.map(area => {
                    const isSelected = this.isAreaOfFocusSelected(area.value);
                    const checkboxColor = isSelected ? 'text-succession-primary' : 'text-succession-text-muted';
                    
                    return (
                                              <div 
                          key={area.value}
                          className={`relative p-4 rounded-lg border ${isSelected ? 'border-succession-primary bg-succession-primary-10' : 'border-succession-border bg-succession-bg-primary'} hover:border-succession-primary transition-all duration-200 group`}
                        >
                        <div 
                          className="flex items-start justify-between cursor-pointer"
                          onClick={() => this.toggleAreaOfFocus(area.value)}
                        >
                          <div className="flex-1">
                            <div className="flex items-center">
                              <div className={`flex-shrink-0 h-5 w-5 ${checkboxColor} transition-colors duration-200`}>
                                <svg className={`h-5 w-5 ${isSelected ? 'opacity-100' : 'opacity-0'} group-hover:opacity-50 transition-opacity duration-200`} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                </svg>
                              </div>
                                                              <div className="ml-3">
                                  <h4 className={`text-sm font-medium ${isSelected ? 'text-succession-primary' : 'text-succession-text-primary'} transition-colors duration-200`}>
                                    {area.label}
                                  </h4>
                                </div>
                            </div>
                          </div>
                        </div>
                        
                        {isSelected && areasOfFocus[area.value] && (
                          <div className="mt-3 pl-8 pr-3">
                            <div className="flex items-center justify-between">
                                                          <span className="text-xs text-succession-text-muted">{this.getTranslatedText('percent.min')}</span>
                            <span className="text-xs font-medium text-succession-primary">
                              {areasOfFocus[area.value]}%
                            </span>
                            <span className="text-xs text-succession-text-muted">{this.getTranslatedText('percent.max')}</span>
                            </div>
                            <input
                              type="range"
                              min="0"
                              max="100"
                              step="5"
                              value={areasOfFocus[area.value]}
                              onClick={(e) => e.stopPropagation()}
                              onMouseDown={(e) => e.stopPropagation()}
                              onChange={(e) => {
                                e.stopPropagation();
                                this.handleAreaPercentageChange(area.value, e.target.value);
                              }}
                              className="w-full h-2 bg-gray-200 border border-succession-border rounded-lg appearance-none cursor-pointer accent-succession-primary mt-1"
                              style={{ accentColor: 'var(--succession-primary, #5489B6)' }}
                            />
                          </div>
                        )}
                      </div>
                    );
                  })
                )}
              </div>
              
              {/* Percentage Total Warning */}
              <div className="mt-4 flex items-center justify-between">
                <div className="flex items-center">
                  <span className="text-sm font-medium text-succession-text-primary mr-2">{this.getTranslatedText('total')}</span>
                  <span className={`text-sm font-bold ${this.calculateTotalAreaPercentage() === 100 ? 'text-green-600' : 'text-amber-600'}`}>
                    {this.calculateTotalAreaPercentage()}%
                  </span>
                </div>
                
                {this.calculateTotalAreaPercentage() !== 100 && (
                  <div className="text-sm text-amber-600 flex items-center">
                    <svg className="h-4 w-4 mr-1" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                    </svg>
                    {this.getTranslatedText('total.warning')}
                  </div>
                )}
              </div>
            </div>
          </div>
          
          {/* Navigation buttons */}
          <div className="flex justify-between items-center mt-6 pt-6 border-t border-succession-border">
            <button
              type="button"
              onClick={this.handleBack}
              className="inline-flex justify-center py-2.5 px-6 border border-succession-border text-sm font-medium rounded-md text-succession-text-secondary bg-succession-bg-primary hover:bg-succession-bg-secondary focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-succession-primary transition-all duration-200"
              style={{ 
                backgroundColor: 'var(--succession-bg-primary, #ffffff)',
                borderColor: 'var(--succession-border, #e5e7eb)',
                '--tw-ring-color': 'var(--succession-primary, #5489B6)',
              }}
            >
              <svg className="w-4 h-4 mr-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
              </svg>
              {this.getTranslatedText('back')}
            </button>
            
            <button 
              onClick={this.handleContinue}
              className={`inline-flex items-center px-5 py-2.5 border border-transparent text-sm font-medium rounded-md shadow-sm text-white transition-all duration-200 ${
                loading || mutateLoading || !formValid 
                  ? 'bg-succession-primary opacity-60 cursor-not-allowed' 
                  : 'bg-succession-primary hover:bg-succession-primary-hover hover:-translate-y-0.5 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-succession-primary'
              }`}
              style={{ 
                backgroundColor: 'var(--succession-primary, #5489B6)',
                '--tw-ring-color': 'var(--succession-primary, #5489B6)',
              }}
              disabled={loading || mutateLoading || !formValid}
            >
              {loading || mutateLoading ? (
                <>
                  <svg className="animate-spin mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  {this.getTranslatedText('saving')}
                </>
              ) : (
                <>
                  {this.getTranslatedText('continue')}
                  <svg className="ml-2 h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                  </svg>
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    );
  }
}

PandCLinesOfBusinessStep.propTypes = {
  user: PropTypes.object,
  onNext: PropTypes.func,
  onUserUpdate: PropTypes.func,
  mutate: PropTypes.func,
  loading: PropTypes.bool,
  onBack: PropTypes.func,
  currentLanguage: PropTypes.string,
  onboardingContext: PropTypes.object
};

// Set default props
PandCLinesOfBusinessStep.defaultProps = {
  currentLanguage: 'en'
};

// Connect component to both GraphQL and OnboardingContext
export default withOnboardingContext(
  graphql(UPDATE_CURRENT_USER, {
    options: { 
      errorPolicy: 'all' 
    }
  })(PandCLinesOfBusinessStep)
); 